/**
 * Cartão genérico de estado (erro / vazio / informativo) — usado em todas as
 * etapas do fluxo para nunca deixar a tela "quieta" quando algo foge do
 * caminho feliz. Composição sobre `Alert` de `@argink/ui` (não uma segunda
 * implementação): reusa role/aria-live corretos por tom em vez de reinventar.
 */
import type { ReactNode } from 'react';
import { Alert } from '@argink/ui';
import type { AlertTone } from '@argink/ui';
import { SupportCode } from './SupportCode';

export type StateTone = 'error' | 'info' | 'empty';

export interface StateCardProps {
  tone: StateTone;
  title: string;
  detail?: string;
  action?: ReactNode;
  children?: ReactNode;
  /** Código de suporte (trace_id) — passar em todo erro vindo de `describeError`.
   * Ausente = não exibe (estados informativos/vazios não têm chamada associada). */
  traceId?: string;
}

/** error -> danger (role="alert", assertivo); info/empty -> status (polido) —
 * mesma regra de urgência que a implementação anterior tinha. */
const TONE_MAP: Record<StateTone, AlertTone> = {
  error: 'danger',
  info: 'info',
  empty: 'neutral',
};

export function StateCard({ tone, title, detail, action, children, traceId }: StateCardProps) {
  return (
    <Alert tone={TONE_MAP[tone]} title={title} className="state-card">
      {detail ? <p className="state-card__detail">{detail}</p> : null}
      {children}
      {traceId ? <SupportCode traceId={traceId} /> : null}
      {action ? <div className="state-card__action">{action}</div> : null}
    </Alert>
  );
}
